import { StoreStateLens } from 'src/providers/StoreStateProvider';
import { createUseStoreState } from 'src/utils/createUseStoreState';
import { LabelsAttachableTo } from 'types/graphql';
import { LabelDef } from './labelDefinitions.store';

export type AttachedLabel = { id: number | undefined; labelDefinition: LabelDef };

export type LabelAttachableTo = LabelsAttachableTo;

export type LabelAttachmentTarget = LabelAttachableTo | null;

export type NewLabelAttachment = {
  color: string;
  label: string;
  id?: number; // Draft labels won't have an ID
};
export function getLabelAttachmentTargetForRoute(
  salesOrder: { id: number; slug: string } | null | undefined,
  slug: string
): LabelAttachmentTarget {
  return salesOrder?.slug === slug ? { salesOrderId: salesOrder.id } : null;
}

export type LabelAttachmentsStore = {
  labels: AttachedLabel[];
  attachLabelsTo: LabelAttachmentTarget;
  newLabelAttachments: NewLabelAttachment[];
  dropdownOpen: boolean;
  showMoreLabelsOpen: boolean;

  attachLabel: (label: LabelDef) => void;
  attachNewLabels: (isRemote?: boolean) => void;
  detachLabel: (id: number) => void;
  replaceLabels: (labels: AttachedLabel[]) => void;
  removeLabels: (labels: string[]) => void;
  addNewLabelAttachment: (draftLabel: NewLabelAttachment) => void;
  removeNewLabelAttachment: (label: string) => void;
  getNewAttachments: () => { newLabelAttachments: NewLabelAttachment[]; attachTo: LabelAttachmentTarget };
  openDropdown: () => void;
  setAttachLabelsTo: (target: LabelAttachmentTarget) => void;
  closeDropdown: () => void;
  openShowMoreLabelsDropdown: () => void;
  closeShowMoreLabelsDropdown: () => void;
  setAttachedLabels: (labels: AttachedLabel[]) => void;
};

export const LABEL_ATTACHMENTS_SLICE = 'labelAttachments';

export type LabelAttachmentsStoreInitialState = { attachLabelsTo?: LabelAttachmentTarget };

export const createLabelAttachmentsSlice: StoreStateLens<LabelAttachmentsStore, LabelAttachmentsStoreInitialState> = (
  set,
  get,
  initialState
) => ({
  labels: [],
  newLabelAttachments: [],
  attachLabelsTo: initialState.attachLabelsTo ?? null,
  dropdownOpen: false,
  showMoreLabelsOpen: false,

  attachLabel: (label: LabelDef) => {
    set((state) => {
      state.labels.push({ id: null, labelDefinition: label });
    });
  },
  attachNewLabels: (isRemote = true) => {
    set((state) => {
      state.labels.push(...state.newLabelAttachments.map((label) => ({ id: null, labelDefinition: label })));

      if (isRemote) {
        state.newLabelAttachments = [];
      }
    });
  },
  detachLabel: (id: number) => {
    set((state) => {
      state.labels = state.labels.filter((label) => label.id !== id);
    });
  },
  replaceLabels: (labels: AttachedLabel[]) => {
    const labelsAsMap = new Map(labels.map((label) => [label.labelDefinition.label, label]));

    set((state) => {
      state.labels = state.labels.map(
        (curAttachment) => labelsAsMap.get(curAttachment.labelDefinition.label) ?? curAttachment
      );
    });
  },
  removeLabels: (labels: string[]) => {
    set((state) => {
      state.labels = state.labels.filter((label) => !labels.includes(label.labelDefinition.label));
    });
  },
  addNewLabelAttachment: (newLabelAttachment: NewLabelAttachment) => {
    set((state) => {
      state.newLabelAttachments.push(newLabelAttachment);
    });
  },
  removeNewLabelAttachment: (labelToRemove: string) => {
    set((state) => {
      state.newLabelAttachments = state.newLabelAttachments.filter(({ label }) => label !== labelToRemove);
    });
  },
  getNewAttachments: () => {
    const state = get();

    return { newLabelAttachments: state.newLabelAttachments, attachTo: state.attachLabelsTo };
  },
  setAttachLabelsTo: (target) => {
    set((state) => {
      if (JSON.stringify(state.attachLabelsTo) === JSON.stringify(target)) {
        return;
      }

      state.attachLabelsTo = target;
      state.labels = [];
      state.newLabelAttachments = [];
    });
  },
  openDropdown: () => {
    set((state) => {
      state.dropdownOpen = true;
    });
  },
  closeDropdown: () => {
    set((state) => {
      state.dropdownOpen = false;
    });
  },
  openShowMoreLabelsDropdown: () => {
    set((state) => {
      state.showMoreLabelsOpen = true;
    });
  },
  closeShowMoreLabelsDropdown: () => {
    set((state) => {
      state.showMoreLabelsOpen = false;
    });
  },
  setAttachedLabels: (labels: AttachedLabel[]) => {
    set((state) => {
      state.labels = labels;
    });
  },
});

export const useLabelAttachmentsStore = createUseStoreState<LabelAttachmentsStore>(LABEL_ATTACHMENTS_SLICE);
